import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { FileText, Save, Download, X, AlertCircle, Lightbulb, Plus } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect } from 'react';
import PflegeplanPreview from './PflegeplanPreview';

interface Patient {
  id?: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  geschlecht: string;
  zimmernummer: string;
  aufnahmedatum: string;
  diagnose: string;
  besonderheiten: string;
}

interface Pflegeplan {
  id?: string;
  patient_id: string;
  titel: string;
  lebensaktivitaet: string;
  ressourcen: string[];
  probleme: string[];
  ziele: string[];
  massnahmen: string[];
}

interface Formulierungshilfe {
  id: string;
  kategorie: string;
  lebensaktivitaet: string;
  text: string;
  stichwort: string;
}

interface PflegeplanEditorProps {
  selectedPatient: Patient | null;
  pflegeplan: Pflegeplan;
  setPflegeplan: (pflegeplan: Pflegeplan | ((prev: Pflegeplan) => Pflegeplan)) => void;
  lebensaktivitaeten: string[];
  onSavePflegeplan: () => void;
  onExportToPDF: () => void;
}

const PflegeplanEditor = ({
  selectedPatient,
  pflegeplan,
  setPflegeplan,
  lebensaktivitaeten,
  onSavePflegeplan,
  onExportToPDF
}: PflegeplanEditorProps) => {
  const [suggestedMassnahmen, setSuggestedMassnahmen] = useState<Formulierungshilfe[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleInputChange = (field: keyof Pflegeplan, value: string) => {
    setPflegeplan(prev => ({ ...prev, [field]: value }));
  };

  const removeItem = (category: 'ressourcen' | 'probleme' | 'ziele' | 'massnahmen', index: number) => {
    setPflegeplan(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const addCustomItem = (category: 'ressourcen' | 'probleme' | 'ziele' | 'massnahmen', text: string) => {
    if (text.trim()) {
      setPflegeplan(prev => ({
        ...prev,
        [category]: [...prev[category], text.trim()]
      }));
    }
  };

  // Funktion zum Laden von Maßnahmen-Vorschlägen basierend auf Problemen
  const loadSuggestedMassnahmen = async () => {
    if (!pflegeplan.lebensaktivitaet || pflegeplan.probleme.length === 0) {
      setSuggestedMassnahmen([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('formulierungshilfen_2025_12_03_21_33')
        .select('*')
        .eq('kategorie', 'massnahme')
        .eq('lebensaktivitaet', pflegeplan.lebensaktivitaet)
        .limit(10);

      if (error) {
        console.error('Fehler beim Laden der Maßnahmen-Vorschläge:', error);
        return;
      }

      // Filtere bereits hinzugefügte Maßnahmen aus
      const filteredSuggestions = data?.filter(suggestion => 
        !pflegeplan.massnahmen.includes(suggestion.text)
      ) || [];

      setSuggestedMassnahmen(filteredSuggestions);
      setShowSuggestions(filteredSuggestions.length > 0);
    } catch (error) {
      console.error('Fehler beim Laden der Maßnahmen-Vorschläge:', error);
    }
  };

  // Lade Vorschläge wenn sich Probleme oder Lebensaktivität ändern
  useEffect(() => {
    loadSuggestedMassnahmen();
  }, [pflegeplan.probleme, pflegeplan.lebensaktivitaet]);

  // Funktion zum Hinzufügen einer vorgeschlagenen Maßnahme
  const addSuggestedMassnahme = (massnahme: Formulierungshilfe) => {
    setPflegeplan(prev => ({
      ...prev,
      massnahmen: [...prev.massnahmen, massnahme.text]
    }));
    // Entferne die hinzugefügte Maßnahme aus den Vorschlägen
    setSuggestedMassnahmen(prev => prev.filter(m => m.id !== massnahme.id));
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'ressourcen':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'probleme':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'ziele':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'massnahmen':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const CategorySection = ({ 
    title, 
    category, 
    items, 
    placeholder 
  }: { 
    title: string; 
    category: 'ressourcen' | 'probleme' | 'ziele' | 'massnahmen'; 
    items: string[]; 
    placeholder: string;
  }) => {
    const [customText, setCustomText] = useState('');

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Badge className={getCategoryColor(category)}>
              {title}
            </Badge>
            <span className="text-sm font-normal text-gray-600">({items.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 ? (
            <p className="text-gray-500 text-sm italic">
              Noch keine {title.toLowerCase()} hinzugefügt. Nutzen Sie die Formulierungshilfen oder fügen Sie eigene hinzu.
            </p>
          ) : (
            <div className="space-y-2">
              {items.map((item, index) => (
                <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="flex-1 text-sm">{item}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeItem(category, index)}
                    className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          <div className="space-y-2">
            <Label className="text-sm">Eigene Formulierung hinzufügen:</Label>
            <div className="flex gap-2">
              <Textarea
                placeholder={placeholder}
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                className="min-h-[60px]"
              />
              <Button
                size="sm"
                onClick={() => {
                  addCustomItem(category, customText);
                  setCustomText('');
                }}
                disabled={!customText.trim()}
              >
                Hinzufügen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Pflegeplan erstellen</h2>
        <p className="text-gray-600">Erstellen Sie einen individuellen Pflegeplan basierend auf den Formulierungshilfen.</p>
      </div>

      {!selectedPatient ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Bitte wählen Sie zuerst einen Patienten aus der Patientenverwaltung aus, um einen Pflegeplan zu erstellen.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {/* Patienteninfo */}
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-blue-900">Patient</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Name:</strong> {selectedPatient.vorname} {selectedPatient.nachname}</div>
                <div><strong>Zimmer:</strong> {selectedPatient.zimmernummer}</div>
                <div><strong>Geburtsdatum:</strong> {selectedPatient.geburtsdatum}</div>
                <div><strong>Geschlecht:</strong> {selectedPatient.geschlecht}</div>
                <div className="col-span-2"><strong>Diagnose:</strong> {selectedPatient.diagnose}</div>
              </div>
            </CardContent>
          </Card>

          {/* Pflegeplan-Grunddaten */}
          <Card>
            <CardHeader>
              <CardTitle>Pflegeplan-Grunddaten</CardTitle>
              <CardDescription>
                Geben Sie die grundlegenden Informationen für den Pflegeplan ein.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="titel">Titel des Pflegeplans *</Label>
                  <Input
                    id="titel"
                    placeholder="z.B. Pflegeplan Mobilisation"
                    value={pflegeplan.titel}
                    onChange={(e) => handleInputChange('titel', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lebensaktivitaet">Lebensaktivität *</Label>
                  <Select 
                    value={pflegeplan.lebensaktivitaet} 
                    onValueChange={(value) => handleInputChange('lebensaktivitaet', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Lebensaktivität wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {lebensaktivitaeten.map((aktivitaet) => (
                        <SelectItem key={aktivitaet} value={aktivitaet}>
                          {aktivitaet}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Pflegeplan-Inhalte */}
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Pflegeplan-Inhalte</h3>
              <p className="text-sm text-gray-600">
                Nutzen Sie die Formulierungshilfen oder fügen Sie eigene Formulierungen hinzu.
              </p>
            </div>

            <div className="grid gap-6">
              <CategorySection
                title="Ressourcen"
                category="ressourcen"
                items={pflegeplan.ressourcen}
                placeholder="Beschreiben Sie vorhandene Fähigkeiten und Stärken des Patienten..."
              />

              <CategorySection
                title="Probleme"
                category="probleme"
                items={pflegeplan.probleme}
                placeholder="Beschreiben Sie identifizierte Pflegeprobleme und Defizite..."
              />

              <CategorySection
                title="Ziele"
                category="ziele"
                items={pflegeplan.ziele}
                placeholder="Formulieren Sie konkrete, messbare Pflegeziele..."
              />

              <CategorySection
                title="Maßnahmen"
                category="massnahmen"
                items={pflegeplan.massnahmen}
                placeholder="Beschreiben Sie konkrete Pflegemaßnahmen und Interventionen..."
              />

              {/* Maßnahmen-Vorschläge */}
              {showSuggestions && suggestedMassnahmen.length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-800">
                      <Lightbulb className="h-5 w-5" />
                      Vorgeschlagene Maßnahmen
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                        {suggestedMassnahmen.length}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="text-amber-700">
                      Basierend auf den ausgewählten Problemen werden folgende Maßnahmen aus der Lebensaktivität "{pflegeplan.lebensaktivitaet}" vorgeschlagen:
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {suggestedMassnahmen.map((massnahme) => (
                        <div key={massnahme.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-200">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{massnahme.text}</p>
                            <p className="text-xs text-gray-600 mt-1">
                              <strong>Stichwort:</strong> {massnahme.stichwort}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => addSuggestedMassnahme(massnahme)}
                            className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Hinzufügen
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-amber-200">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowSuggestions(false)}
                        className="text-amber-700 hover:bg-amber-100"
                      >
                        Vorschläge ausblenden
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Aktionen */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* PDF Export mit Vorschau */}
                <div>
                  <h4 className="text-sm font-medium mb-3">PDF-Export</h4>
                  <PflegeplanPreview
                    selectedPatient={selectedPatient}
                    pflegeplan={pflegeplan}
                    setPflegeplan={setPflegeplan}
                    onExportToPDF={onExportToPDF}
                  />
                </div>
                
                <Separator />
                
                {/* Weitere Aktionen */}
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={onExportToPDF}
                    className="flex items-center gap-2"
                    disabled={!pflegeplan.titel || !pflegeplan.lebensaktivitaet}
                  >
                    <Download className="h-4 w-4" />
                    Als Textdatei exportieren
                  </Button>
                  <Button
                    onClick={onSavePflegeplan}
                    className="flex items-center gap-2"
                    disabled={!pflegeplan.titel || !pflegeplan.lebensaktivitaet}
                  >
                    <Save className="h-4 w-4" />
                    Pflegeplan speichern
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-right">
                * Pflichtfelder müssen ausgefüllt werden
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default PflegeplanEditor;